import { humanize } from "@/lib/format";

type StatusVariant = "draft" | "pending" | "approved" | "confirmed" | "allocated" | "partially_allocated" | "ready_to_pick" | "partially_fulfilled" | "returned" | "partially_returned" | "in_transit" | "in_progress" | "picked" | "partial" | "fulfilled" | "received" | "completed" | "closed" | "cancelled" | "active" | "inactive" | "paused" | "archived" | "low" | "out" | "warning" | "info" | "success" | "error" | "shipped" | "delivered" | "rated" | "packed" | "ready" | "label_printed" | "pending_carrier" | "exception" | "authorized" | "not_configured" | "expired" | "revoked" | "connected" | "healthy" | "open" | "acknowledged" | "resolved" | "ignored";

const variantMap: Record<StatusVariant, { bg: string; text: string; label: string }> = {
  draft: { bg: "bg-slate-100", text: "text-slate-700", label: "Draft" },
  pending: { bg: "bg-amber-50", text: "text-amber-700", label: "Pending" },
  approved: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Approved" },
  confirmed: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Confirmed" },
  allocated: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Allocated" },
  partially_allocated: { bg: "bg-amber-50", text: "text-amber-700", label: "Partially Allocated" },
  ready_to_pick: { bg: "bg-amber-50", text: "text-amber-700", label: "Ready to Pick" },
  partially_fulfilled: { bg: "bg-orange-50", text: "text-orange-700", label: "Partially Fulfilled" },
  returned: { bg: "bg-rose-50", text: "text-rose-700", label: "Returned" },
  partially_returned: { bg: "bg-orange-50", text: "text-orange-700", label: "Partially Returned" },
  in_transit: { bg: "bg-amber-50", text: "text-amber-700", label: "In Transit" },
  in_progress: { bg: "bg-amber-50", text: "text-amber-700", label: "In Progress" },
  picked: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Picked" },
  shipped: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Shipped" },
  delivered: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Delivered" },
  partial: { bg: "bg-orange-50", text: "text-orange-700", label: "Partial" },
  fulfilled: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Fulfilled" },
  received: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Received" },
  completed: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Completed" },
  closed: { bg: "bg-slate-100", text: "text-slate-700", label: "Closed" },
  cancelled: { bg: "bg-red-50", text: "text-red-700", label: "Cancelled" },
  active: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Active" },
  inactive: { bg: "bg-slate-100", text: "text-slate-500", label: "Inactive" },
  paused: { bg: "bg-amber-50", text: "text-amber-700", label: "Paused" },
  archived: { bg: "bg-slate-100", text: "text-slate-500", label: "Archived" },
  low: { bg: "bg-amber-50", text: "text-amber-700", label: "Low Stock" },
  out: { bg: "bg-red-50", text: "text-red-700", label: "Out of Stock" },
  warning: { bg: "bg-amber-50", text: "text-amber-700", label: "Warning" },
  info: { bg: "bg-slate-100", text: "text-slate-700", label: "Info" },
  success: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Success" },
  error: { bg: "bg-red-50", text: "text-red-700", label: "Error" },
  rated: { bg: "bg-amber-50", text: "text-amber-700", label: "Rated" },
  packed: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Packed" },
  ready: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Ready" },
  label_printed: { bg: "bg-slate-100", text: "text-slate-700", label: "Label Printed" },
  pending_carrier: { bg: "bg-amber-50", text: "text-amber-700", label: "Awaiting Carrier" },
  exception: { bg: "bg-red-50", text: "text-red-700", label: "Exception" },
  authorized: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Authorized" },
  not_configured: { bg: "bg-slate-100", text: "text-slate-500", label: "Not Configured" },
  expired: { bg: "bg-amber-50", text: "text-amber-700", label: "Expired" },
  revoked: { bg: "bg-red-50", text: "text-red-700", label: "Revoked" },
  connected: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Connected" },
  healthy: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Healthy" },
  open: { bg: "bg-red-50", text: "text-red-700", label: "Open" },
  acknowledged: { bg: "bg-amber-50", text: "text-amber-700", label: "Acknowledged" },
  resolved: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Resolved" },
  ignored: { bg: "bg-slate-100", text: "text-slate-500", label: "Ignored" },
};

interface StatusBadgeProps {
  status: StatusVariant | string;
  customLabel?: string;
}

export function StatusBadge({ status, customLabel }: StatusBadgeProps) {
  const config = variantMap[status as StatusVariant] || { bg: "bg-slate-100", text: "text-slate-700", label: humanize(status) };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {customLabel || config.label}
    </span>
  );
}